import React, { useEffect } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useCommonForm } from './hooks/useCommonForm';
import { ImageManager } from '@components/attachments';
import ErrorDisplay from './components/ErrorDisplay';
import BorrowingFieldsManager from '@components/admin/BorrowingFieldsManager';

const CategoryForm = ({ data, category, onSubmit, onCancel, isEditing = false, loading: externalLoading = false }) => {
  const { t } = useLanguage();
  // Support both `data` (from common components) and `category` (legacy)
  const modelData = data || category;

  const initialFormData = {
    name: modelData?.name || '',
    description: modelData?.description || '',
    color: modelData?.color || '#007bff',
    borrowing_field_definitions: modelData?.borrowing_field_definitions || {}
  };

  const validationRules = {
    name: {
      required: true,
      requiredMessage: t('categories.categoryForm.nameRequired'),
      minLength: 1,
      minLengthMessage: t('categories.categoryForm.nameMinLength')
    },
    color: {
      pattern: /^#[0-9A-F]{6}$/i,
      patternMessage: t('categories.categoryForm.colorPattern')
    }
  };

  const {
    formData,
    loading,
    error,
    fieldErrors,
    handleChange,
    handleSubmit,
    updateFormData,
    getFieldProps
  } = useCommonForm({
    initialData: initialFormData,
    onSubmit,
    externalLoading,
    validationRules
  });

  // Update form data when modelData changes
  useEffect(() => {
    if (modelData) {
      const newFormData = {
        name: modelData.name || '',
        description: modelData.description || '',
        color: modelData.color || '#007bff',
        borrowing_field_definitions: modelData.borrowing_field_definitions || {}
      };
      updateFormData(newFormData);
    }
  }, [modelData]);

  return (
    <form className="sloot-form" onSubmit={handleSubmit}>
      <ErrorDisplay error={error} fieldErrors={fieldErrors} />

      <div className="form-group">
        <label htmlFor="name">
          {t('categories.categoryForm.name')} *
        </label>
        <input
          type="text"
          id="name"
          {...getFieldProps('name', {
            required: true,
            placeholder: t('categories.categoryForm.namePlaceholder')
          })}
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">
          {t('categories.categoryForm.description')}
        </label>
        <textarea
          id="description"
          {...getFieldProps('description', {
            placeholder: t('categories.categoryForm.descriptionPlaceholder'),
            rows: 3
          })}
        />
      </div>

      <div className="form-group">
        <label htmlFor="color">
          {t('categories.categoryForm.color')}
        </label>
        <div className="color-input-group">
          <input
            type="color"
            id="color"
            {...getFieldProps('color')}
          />
          <input
            type="text"
            {...getFieldProps('color', {
              placeholder: '#007bff',
              className: 'color-text-input'
            })}
          />
        </div>
      </div>

      {/* Category Images Section - only show if we have a category ID (editing) */}
      {isEditing && modelData?.id && (
        <div className="form-group">
            <label>
                {t('categories.images')}
            </label>
          <ImageManager
            entity={modelData}
            editable={true}
            showUploader={true}
            maxImages={5}
            className="compact"
            maxFileSize={5 * 1024 * 1024} // 5MB for category images
          />
        </div>
      )}

      {/* Borrowing Fields Manager - only show when editing */}
      {isEditing && modelData?.id && (
        <div className="form-group">
          <BorrowingFieldsManager
            fieldDefinitions={formData.borrowing_field_definitions}
            onChange={(definitions) => {
              updateFormData({ ...formData, borrowing_field_definitions: definitions });
            }}
          />
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={loading}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? t('common.saving') : (isEditing ? (t('common.update')) : (t('common.create')))}
        </button>
      </div>
    </form>
  );
};

export default CategoryForm;
